import type { Column, Row, RowData } from '@tanstack/react-table';
import { STRING_ARRAY_FILTER_OPERATION } from './operation.js';
import { type StringArrayFilterSchema, stringArrayFilterSchema, stringArrayValueSchema } from './schema.js';

export function typedSetStringArrayColumnFilterValue<TData extends RowData>(
    column: Column<TData, unknown>,
    filterValue: StringArrayFilterSchema
) {
    column.setFilterValue(filterValue);
}

export function stringArrayFilter<TData extends RowData>(
    row: Row<TData>,
    columnId: string,
    filterValue: StringArrayFilterSchema
) {
    const value = stringArrayValueSchema.parse(row.getValue(columnId)) ?? [];
    const filter = stringArrayFilterSchema.parse(filterValue);

    if (filter.operation === STRING_ARRAY_FILTER_OPERATION.inArray.id) {
        const isBlank =
            value === null ||
            value === undefined ||
            value.length === 0 ||
            value.every((v) => v === null || v === undefined);

        if (filter.includeBlanks && isBlank) {
            return true;
        }

        if (isBlank) {
            return false;
        }

        return filter.array.some((item) => {
            return value.some((val) => {
                return val?.trim().toLowerCase() === item?.trim().toLowerCase();
            });
        });
    }

    if (value.every((val) => val === null || val === undefined)) {
        // TODO: Decide how we handle empty values

        return false;
    } else if (filter.value === null || filter.value === undefined) {
        return true;
    }

    const trimmedFilterValue = filter.value.trim().toLowerCase();

    switch (filter.operation) {
        case STRING_ARRAY_FILTER_OPERATION.beginsWith.id: {
            return value.some((arrayEntry) => arrayEntry?.toLowerCase().startsWith(trimmedFilterValue));
        }
        case STRING_ARRAY_FILTER_OPERATION.endsWith.id: {
            return value.some((arrayEntry) => arrayEntry?.toLowerCase().endsWith(trimmedFilterValue));
        }
        case STRING_ARRAY_FILTER_OPERATION.containsLoose.id: {
            return trimmedFilterValue.split(' ').some((filterEntry) => {
                return value.some((arrayEntry) => arrayEntry?.toLowerCase().includes(filterEntry.toLowerCase()));
            });
        }
        case STRING_ARRAY_FILTER_OPERATION.containsStrict.id: {
            return trimmedFilterValue.split(' ').every((filterEntry) => {
                return value.some((arrayEntry) => arrayEntry?.toLowerCase().includes(filterEntry.toLowerCase()));
            });
        }
        case STRING_ARRAY_FILTER_OPERATION.doesNotContain.id: {
            return !value.some((arrayEntry) => arrayEntry?.toLowerCase().includes(trimmedFilterValue));
        }
    }
}
